import {
  AMBIENT_SCRIBE_GETTING_STARTED,
  PAYWALL_BYPASS
} from "constants/feature-flags";
import { AMBIENT_SCRIBE_FEATURE_KEY } from "constants/paywall";
import type { PaywallFeaturesStatus } from "services/types/paywall.type";

export interface ScribeTemplateAccess {
  isAdmin: boolean;
  isAudiologist: boolean;
  /** `AMBIENT_SCRIBE_GETTING_STARTED`: off, templates stay with admins as before. */
  isGettingStartedOn: boolean;
  /** This user can record with Scribe: a seat, a live trial, or the paywall bypassed. */
  canUseScribe: boolean;
}

/**
 * Who may open Scribe Settings and manage the practice's Scribe templates: admins, as always, and
 * (with the guide's flag on) audiologists who use Scribe themselves. Templates shape the notes the
 * audiologist reviews, and the backend already lets any practice staff write them (the template
 * viewset and the appointment-type link both take `IsPracticeStaffUser`).
 */
export function canManageScribeTemplates({
  isAdmin,
  isAudiologist,
  isGettingStartedOn,
  canUseScribe
}: ScribeTemplateAccess): boolean {
  return isAdmin || (isAudiologist && isGettingStartedOn && canUseScribe);
}

/**
 * The test `useCheckPaywallFeatureAvailability` makes before Capture Visit, read from the features
 * loaded at login (`auth.features`) instead of a fresh request. Those load just after the user, so
 * a route deciding on a cold load must not use this: it reads "no Scribe" until they arrive.
 */
export function canUseScribeFromFeatures(
  flags: Record<string, unknown>,
  features: PaywallFeaturesStatus | undefined
): boolean {
  // Same default as the check itself: a missing bypass flag means bypass.
  const paywallBypassEnabled = flags[PAYWALL_BYPASS] ?? true;
  return (
    Boolean(paywallBypassEnabled) ||
    features?.[AMBIENT_SCRIBE_FEATURE_KEY]?.active === true
  );
}

/** Both rules over the flags, roles and features a caller already holds. */
export function canManageScribeTemplatesFor(
  flags: Record<string, unknown>,
  roles: { isAdminOrBusinessOwner: boolean; isAudiologist: boolean },
  features: PaywallFeaturesStatus | undefined
): boolean {
  return canManageScribeTemplates({
    isAdmin: roles.isAdminOrBusinessOwner,
    isAudiologist: roles.isAudiologist,
    isGettingStartedOn: flags[AMBIENT_SCRIBE_GETTING_STARTED] === true,
    canUseScribe: canUseScribeFromFeatures(flags, features)
  });
}
